import React, { useState } from 'react';
import { nis2Services } from '../data/services';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  createdAt: Date;
}

const Nis2 = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    createdAt: new Date(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, 'applications'), {
        ...formData,
        createdAt: new Date()
      });
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', phone: '', message: '', createdAt: new Date() });
      
      // Opcionális: küldjünk értesítő emailt magunknak
      // Ezt később implementálhatjuk Firebase Cloud Functions segítségével
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      // 5 másodperc múlva töröljük az állapotüzenetet
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-center text-white mb-8">
        NIS 2 Tanácsadás
      </h1>
      
      <div className="bg-gray-800 rounded-xl p-6 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-white mb-4">Mi az a NIS 2?</h2>
        <p className="text-gray-300 mb-4">
          A NIS 2 (Network and Information Security 2) az Európai Unió új kiberbiztonsági irányelve, 
          amely 2022 decemberében lépett hatályba, és 2024 októberéig kell a tagállamoknak átültetniük 
          a nemzeti jogrendszerükbe. Az irányelv célja a kritikus infrastruktúrák és szolgáltatások 
          kiberbiztonságának megerősítése az EU-ban.
        </p>
        <p className="text-gray-300 mb-4">
          A NIS 2 jelentősen kibővíti a szabályozás hatálya alá tartozó szervezetek körét, 
          és szigorúbb biztonsági követelményeket, valamint jelentéstételi kötelezettségeket ír elő.
        </p>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-white mb-4">Kire vonatkozik?</h2>
        <p className="text-gray-300 mb-4">
          A NIS 2 irányelv két kategóriába sorolja a szervezeteket:
        </p>
        <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
          <li><span className="font-semibold text-purple-400">Alapvető szervezetek:</span> Energia, közlekedés, bankok, pénzügyi piaci infrastruktúrák, egészségügy, ivóvíz, szennyvíz, digitális infrastruktúra, űr, közigazgatás</li>
          <li><span className="font-semibold text-purple-400">Fontos szervezetek:</span> Posta, hulladékgazdálkodás, vegyipar, élelmiszer, gyártás, digitális szolgáltatók, kutatás</li>
        </ul>
        <p className="text-gray-300">
          Az irányelv általában a közepes és nagyvállalatokra vonatkozik (több mint 50 alkalmazott vagy 10 millió EUR éves árbevétel felett).
        </p>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-white mb-4">Szolgáltatásaink</h2>
        <p className="text-gray-300 mb-6">
          Szakértői csapatunk segít a NIS 2 megfelelőség elérésében és fenntartásában:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {nis2Services.map((service, index) => (
            <div key={index} className="flex items-start">
              <svg className="w-5 h-5 text-purple-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-300">{service}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 shadow-lg mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">Miért válasszon minket?</h2>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start">
            <svg className="w-5 h-5 text-purple-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Több éves tapasztalat az IT biztonság területén</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-purple-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Naprakész ismeretek a jogszabályi követelményekről</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-purple-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Személyre szabott megoldások az Ön vállalkozásának méretéhez és iparágához igazítva</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-purple-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Folyamatos támogatás a megfelelőség fenntartásában</span>
          </li>
        </ul>
      </div>

      {/* Kapcsolatfelvételi űrlap */}
      <div id="kapcsolat" className="bg-gradient-to-r from-purple-800 to-indigo-900 rounded-xl shadow-lg p-8 text-white text-center mb-12">
        <h2 className="text-2xl font-bold mb-4">Kérjen ingyenes NIS2 konzultációt</h2>
        <p className="mb-6 text-purple-100">
          Adja meg elérhetőségeit és szakértő kollégánk hamarosan felveszi Önnel a kapcsolatot
        </p>
        
        {/* Jelentkezési űrlap */}
        <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Konzultációs igény beküldése</h3>
          
          {submitStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-100">
              Jelentkezését sikeresen rögzítettük! Hamarosan felvesszük Önnel a kapcsolatot.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-100">
              Hiba történt a jelentkezés során. Kérjük, próbálja újra később!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Kapcsolattartó neve"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="E-mail cím"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Cég neve"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Telefonszám"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Üzenet (opcionális)"
              rows={4}
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full md:w-auto px-8 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors duration-200 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Küldés...' : 'Konzultációt kérek'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default Nis2; 